/**
 * The stacking order for every Sellar app, in one place. z-index only compares
 * elements within a stacking context, so values scattered across components
 * can never be reasoned about locally.
 *
 * Only globally-stacked elements belong here: anything `fixed`, or portalled
 * to the body. z-index that is local to a component — a sticky table header in
 * its own scroll container, a badge over an image — competes only with its own
 * siblings, so a plain `z-10` is clearer than a token. Keep those at 50 or
 * below; `sellar/no-raw-z-index` enforces the split. Layers are spaced by 100
 * so a new one can be slotted between two existing ones.
 */
export const zIndexLayer = {
  content: 0,
  /** Pinned to the viewport while content scrolls under it: sticky filter
   *  bars, fixed CTA footers, floating action buttons. */
  pinned: 100,
  banner: 200,
  /** Under the bar it belongs to, over everything the page scrolls. */
  navigationSubBar: 290,
  navigationBar: 300,
  drawer: 400,
  /** Above drawers: a dialog opened from a drawer must cover it. */
  dialog: 500,
  toast: 800,
  dev: 900,
} as const;

export type ZIndexLayerName = keyof typeof zIndexLayer;

/** Spelled out rather than built from the numbers, so Tailwind's scanner sees them. */
export const zLayer = {
  content: "z-0",
  pinned: "z-100",
  banner: "z-200",
  navigationSubBar: "z-290",
  navigationBar: "z-300",
  drawer: "z-400",
  dialog: "z-500",
  toast: "z-800",
  dev: "z-900",
} as const satisfies Record<ZIndexLayerName, string>;

/**
 * Floating UI cannot take a single global layer. It portals out of its
 * trigger, so it has to clear whatever it was opened inside; but it must not
 * vault over chrome it was never part of. A tooltip in a dialog belongs above
 * that dialog; the same tooltip on a page button stays under the nav bar and
 * its sub-menus — floating-ui flips and shifts to keep it visible, so sitting
 * below that chrome costs nothing.
 *
 * Each host therefore reserves the band just above itself. Components read it
 * through `useFloatingLayer`, and a container declares its host once — see
 * floatingLayer.tsx. A pinned bar only needs to declare one if it contains
 * floating UI of its own; otherwise leaving it alone is what keeps it above
 * page tooltips.
 */
export type ZFloatingHost =
  | "content"
  | "pinned"
  | "navigationBar"
  | "drawer"
  | "dialog";

export const zIndexFloating = {
  content: { popover: 50, tooltip: 60 },
  pinned: { popover: 150, tooltip: 160 },
  navigationBar: { popover: 350, tooltip: 360 },
  drawer: { popover: 450, tooltip: 460 },
  dialog: { popover: 550, tooltip: 560 },
} as const satisfies Record<
  ZFloatingHost,
  { popover: number; tooltip: number }
>;

export const zFloating = {
  content: { popover: "z-50", tooltip: "z-60" },
  pinned: { popover: "z-150", tooltip: "z-160" },
  navigationBar: { popover: "z-350", tooltip: "z-360" },
  drawer: { popover: "z-450", tooltip: "z-460" },
  dialog: { popover: "z-550", tooltip: "z-560" },
} as const satisfies Record<
  ZFloatingHost,
  { popover: string; tooltip: string }
>;
